import { c as _c } from "react/compiler-runtime";
import type { DocumentViewerCard, TabGroupKind } from '@onefootprint/request-types/dashboard';
import { createParser, parseAsBoolean, parseAsInteger, useQueryState } from 'nuqs';
import { useEffect } from 'react';
import { groupCardsByTab } from '../utils';
type UseViewerStateProps = {
  cards: DocumentViewerCard[];
};
const TAB_ORDER: TabGroupKind[] = ['validation', 'extraction', 'tamper_detection', 'classification', 'vault_data'];
const parseAsTab = createParser<TabGroupKind>({
  parse: value => TAB_ORDER.includes(value as TabGroupKind) ? value as TabGroupKind : null,
  serialize: value => value
});
const parseAsStringSet = createParser<Set<string>>({
  parse: value => value ? new Set(value.split(',').filter(Boolean)) : new Set(),
  serialize: value => Array.from(value).join(',')
});
const useViewerState = t0 => {
  const $ = _c(21);
  const {
    cards
  } = t0;
  const groupedCards = groupCardsByTab(cards);
  const hasAnyContent = TAB_ORDER.some(tab => groupedCards[tab].length > 0);
  const getDefaultTab = () => {
    for (const tab_0 of TAB_ORDER) {
      if (groupedCards[tab_0].length > 0) {
        return tab_0;
      }
    }
    return "validation";
  };
  let t1;
  if ($[0] !== getDefaultTab) {
    t1 = parseAsTab.withDefault(getDefaultTab()).withOptions({
      clearOnDefault: false
    });
    $[0] = getDefaultTab;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const [selectedTab, setSelectedTab] = useQueryState("tab", t1);
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = parseAsStringSet.withDefault(new Set());
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const [selectedBboxIds, setSelectedBboxIds] = useQueryState("bboxIds", t2);
  const [scrollToPageId, setScrollToPageId] = useQueryState("page", parseAsInteger);
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = parseAsBoolean.withDefault(true);
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const [isCenterPanelOpen, setIsCenterPanelOpen] = useQueryState("panel", t3);
  let t4;
  let t5;
  if ($[4] !== cards.length || $[5] !== hasAnyContent || $[6] !== setIsCenterPanelOpen) {
    t4 = () => {
      if (cards.length > 0 && !hasAnyContent) {
        setIsCenterPanelOpen(false);
      }
    };
    t5 = [cards.length, hasAnyContent, setIsCenterPanelOpen];
    $[4] = cards.length;
    $[5] = hasAnyContent;
    $[6] = setIsCenterPanelOpen;
    $[7] = t4;
    $[8] = t5;
  } else {
    t4 = $[7];
    t5 = $[8];
  }
  useEffect(t4, t5);
  const ids = [];
  for (const card of groupedCards[selectedTab]) {
    if (card.content.contentType === "bounding_box") {
      for (const group of card.content.bboxGroups) {
        for (const bbox of group.bboxes) {
          ids.push(bbox.id);
        }
      }
    }
  }
  const currentTabBboxIds = ids;
  let t6;
  if ($[9] !== selectedBboxIds || $[10] !== setScrollToPageId || $[11] !== setSelectedBboxIds) {
    t6 = (id, pageId) => {
      const isCurrentlySelected = selectedBboxIds.has(id);
      setSelectedBboxIds(prev => {
        const next = new Set(prev);
        next.has(id) ? next.delete(id) : next.add(id);
        return next;
      });
      if (pageId !== undefined && !isCurrentlySelected) {
        setScrollToPageId(pageId);
      }
    };
    $[9] = selectedBboxIds;
    $[10] = setScrollToPageId;
    $[11] = setSelectedBboxIds;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const toggleBboxSelection = t6;
  const selectAllBboxes = () => {
    setSelectedBboxIds(new Set(currentTabBboxIds));
  };
  let t7;
  if ($[13] !== setSelectedBboxIds) {
    t7 = () => {
      setSelectedBboxIds(new Set());
    };
    $[13] = setSelectedBboxIds;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  const clearBboxSelection = t7;
  let t8;
  if ($[15] !== setSelectedBboxIds) {
    t8 = ids_0 => {
      setSelectedBboxIds(prev_0 => {
        const next_0 = new Set(prev_0);
        for (const id_0 of ids_0) {
          next_0.add(id_0);
        }
        return next_0;
      });
    };
    $[15] = setSelectedBboxIds;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  const selectBboxes = t8;
  let t9;
  if ($[17] !== setSelectedBboxIds) {
    t9 = ids_1 => {
      setSelectedBboxIds(prev_1 => {
        const next_1 = new Set(prev_1);
        for (const id_1 of ids_1) {
          next_1.delete(id_1);
        }
        return next_1;
      });
    };
    $[17] = setSelectedBboxIds;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  const clearBboxes = t9;
  let t10;
  if ($[19] !== setIsCenterPanelOpen) {
    t10 = () => {
      setIsCenterPanelOpen(_temp);
    };
    $[19] = setIsCenterPanelOpen;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  const toggleCenterPanel = t10;
  return {
    selectedTab,
    setSelectedTab,
    selectedBboxIds,
    toggleBboxSelection,
    selectAllBboxes,
    clearBboxSelection,
    selectBboxes,
    clearBboxes,
    groupedCards,
    currentTabBboxIds,
    scrollToPageId,
    isCenterPanelOpen,
    toggleCenterPanel,
    hasAnyContent
  };
};
export default useViewerState;
function _temp(prev_2) {
  return !prev_2;
}